import { useState } from 'react';
import {
  MessageSquare,
  Search,
  Mail,
  Phone,
  Calendar,
  Check,
  X,
  Star,
  Reply,
  Trash2,
  Archive,
  MoreVertical,
} from 'lucide-react';
import { cn } from '@/lib/cn';

interface Message {
  id: string;
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
  status: 'unread' | 'read' | 'replied' | 'archived';
  starred: boolean;
  createdAt: Date;
}

// Demo data
const initialMessages: Message[] = [
  {
    id: '1',
    name: 'Ahmet Yilmaz',
    email: 'ahmet@email.com',
    phone: '+90 532 123 4567',
    subject: 'Fiyat Bilgisi',
    message: 'Merhaba, web sitesi gelistirme hizmetleriniz hakkinda fiyat bilgisi almak istiyorum. Orta olcekli bir e-ticaret projesi dusunuyoruz.',
    status: 'unread',
    starred: true,
    createdAt: new Date('2024-01-18T10:30:00'),
  },
  {
    id: '2',
    name: 'Fatma Demir',
    email: 'fatma@firma.com',
    subject: 'Is Birligi Teklifi',
    message: 'Sirketimiz icin dijital donusum projesi planliyoruz. Sizinle gorusmek isteriz. Musait oldugunuz bir zaman belirleyebilir misiniz?',
    status: 'read',
    starred: false,
    createdAt: new Date('2024-01-17T14:15:00'),
  },
  {
    id: '3',
    name: 'Mehmet Kaya',
    email: 'mehmet@startup.io',
    phone: '+90 533 987 6543',
    subject: 'Teknik Destek',
    message: 'Daha once gelistirdiginiz mobil uygulama icin teknik destek almak istiyoruz. Bazi hatalarla karsilasiyoruz.',
    status: 'replied',
    starred: false,
    createdAt: new Date('2024-01-16T09:00:00'),
  },
  {
    id: '4',
    name: 'Zeynep Arslan',
    email: 'zeynep@company.com',
    subject: 'Referans Talebi',
    message: 'Sizin daha once calistiginiz firmalardan referans alabilir miyiz? Karar verme surecimiz icin faydali olacaktir.',
    status: 'archived',
    starred: false,
    createdAt: new Date('2024-01-10T16:45:00'),
  },
];

export function AdminMessages() {
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);

  // Filter messages
  const filteredMessages = messages.filter(m => {
    const matchesSearch = m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.message.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || m.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const updateStatus = (id: string, status: Message['status']) => {
    setMessages(messages.map(m => m.id === id ? { ...m, status } : m));
    if (selectedMessage?.id === id) {
      setSelectedMessage({ ...selectedMessage, status });
    }
  };

  const toggleStar = (id: string) => {
    setMessages(messages.map(m => m.id === id ? { ...m, starred: !m.starred } : m));
    if (selectedMessage?.id === id) {
      setSelectedMessage({ ...selectedMessage, starred: !selectedMessage.starred });
    }
  };

  const deleteMessage = (id: string) => {
    setMessages(messages.filter(m => m.id !== id));
    if (selectedMessage?.id === id) {
      setSelectedMessage(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'unread': return 'bg-blue-500';
      case 'read': return 'bg-gray-400';
      case 'replied': return 'bg-green-500';
      case 'archived': return 'bg-yellow-500';
      default: return 'bg-gray-400';
    }
  };

  const stats = {
    total: messages.length,
    unread: messages.filter(m => m.status === 'unread').length,
    starred: messages.filter(m => m.starred).length,
  };

  const formatDate = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (hours < 24) {
      return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('tr-TR');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Iletisim Mesajlari</h1>
        <p className="text-text-secondary">Iletisim formundan gelen mesajlari yonetin</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-accent">{stats.total}</p>
          <p className="text-sm text-text-secondary">Toplam Mesaj</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-blue-500">{stats.unread}</p>
          <p className="text-sm text-text-secondary">Okunmamis</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-yellow-500">{stats.starred}</p>
          <p className="text-sm text-text-secondary">Yildizli</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" size={18} />
          <input
            type="text"
            placeholder="Mesajlarda ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg bg-bg-card border border-border-default focus:border-accent focus:outline-none"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 rounded-lg bg-bg-card border border-border-default focus:border-accent focus:outline-none"
        >
          <option value="all">Tum Mesajlar</option>
          <option value="unread">Okunmamis</option>
          <option value="read">Okunmus</option>
          <option value="replied">Yanitlanmis</option>
          <option value="archived">Arsivlenmis</option>
        </select>
      </div>

      <div className="flex gap-6">
        {/* Messages List */}
        <div className="flex-1 bg-bg-card border border-border-default rounded-xl overflow-hidden">
          <div className="divide-y divide-border-default">
            {filteredMessages.map(message => (
              <div
                key={message.id}
                onClick={() => { setSelectedMessage(message); if (message.status === 'unread') updateStatus(message.id, 'read'); }}
                className={cn(
                  'p-4 cursor-pointer transition-colors',
                  selectedMessage?.id === message.id ? 'bg-accent/5' : 'hover:bg-bg-elevated/50',
                  message.status === 'unread' && 'bg-blue-500/5'
                )}
              >
                <div className="flex items-start gap-3">
                  <div className={cn('w-2 h-2 rounded-full mt-2', getStatusColor(message.status))} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className={cn('font-medium truncate', message.status === 'unread' ? 'text-text-primary' : 'text-text-secondary')}>
                        {message.name}
                      </span>
                      <span className="text-xs text-text-tertiary flex-shrink-0">{formatDate(message.createdAt)}</span>
                    </div>
                    <p className={cn('text-sm truncate mb-1', message.status === 'unread' ? 'text-text-primary font-medium' : 'text-text-secondary')}>
                      {message.subject}
                    </p>
                    <p className="text-xs text-text-tertiary truncate">{message.message}</p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleStar(message.id); }}
                    className={cn('p-1', message.starred ? 'text-yellow-500' : 'text-text-tertiary hover:text-yellow-500')}
                  >
                    <Star size={16} fill={message.starred ? 'currentColor' : 'none'} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredMessages.length === 0 && (
            <div className="text-center py-12">
              <MessageSquare className="mx-auto mb-4 text-text-tertiary" size={48} />
              <p className="text-text-tertiary">Mesaj bulunamadi</p>
            </div>
          )}
        </div>

        {/* Message Detail */}
        {selectedMessage && (
          <div className="w-96 bg-bg-card border border-border-default rounded-xl h-fit sticky top-6">
            <div className="p-4 border-b border-border-default flex items-center justify-between">
              <h3 className="font-semibold text-text-primary">Mesaj Detayi</h3>
              <button onClick={() => setSelectedMessage(null)} className="p-1 rounded hover:bg-bg-elevated">
                <X size={18} className="text-text-tertiary" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* Sender Info */}
              <div>
                <p className="text-lg font-semibold text-text-primary">{selectedMessage.name}</p>
                <a href={`mailto:${selectedMessage.email}`} className="flex items-center gap-2 text-sm text-accent hover:underline">
                  <Mail size={14} /> {selectedMessage.email}
                </a>
                {selectedMessage.phone && (
                  <a href={`tel:${selectedMessage.phone}`} className="flex items-center gap-2 text-sm text-text-secondary hover:text-accent">
                    <Phone size={14} /> {selectedMessage.phone}
                  </a>
                )}
              </div>

              {/* Subject */}
              <div>
                <p className="text-xs text-text-tertiary mb-1">Konu</p>
                <p className="text-text-primary font-medium">{selectedMessage.subject}</p>
              </div>

              {/* Date */}
              <div className="flex items-center gap-2 text-sm text-text-tertiary">
                <Calendar size={14} />
                {selectedMessage.createdAt.toLocaleString('tr-TR')}
              </div>

              {/* Message */}
              <div>
                <p className="text-xs text-text-tertiary mb-2">Mesaj</p>
                <div className="p-3 rounded-lg bg-bg-elevated">
                  <p className="text-text-primary whitespace-pre-wrap">{selectedMessage.message}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-4 border-t border-border-default">
                <a
                  href={`mailto:${selectedMessage.email}?subject=RE: ${selectedMessage.subject}`}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-accent text-white hover:bg-accent/90 text-sm"
                >
                  <Reply size={16} /> Yanitla
                </a>
                <button
                  onClick={() => updateStatus(selectedMessage.id, 'archived')}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-bg-elevated text-text-secondary hover:text-text-primary text-sm"
                >
                  <Archive size={16} /> Arsivle
                </button>
                <button
                  onClick={() => deleteMessage(selectedMessage.id)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20 text-sm"
                >
                  <Trash2 size={16} /> Sil
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
